export default function AboutUs() {
  return (
    <div className="min-h-screen bg-[#f9f5e9] text-ink">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10">
        <a
          href="/"
          className="mb-8 inline-block text-sm font-medium text-ink/70 hover:text-ink"
        >
          ← Back to Oriscen
        </a>

        <header className="mb-10">
          <h1
            className="mb-3 text-5xl font-bold tracking-tight"
            style={{
              fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
              color: '#3A331D',
              lineHeight: 1.05,
            }}
          >
            Ori: The easiest way to experience your city.
          </h1>
          <p
            className="text-xl text-ink/70"
            style={{ fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif' }}
          >
            Ori is an AI that lives in your text messages and knows what&rsquo;s good nearby.
            Ask it where to eat, what to do tonight, or what&rsquo;s actually worth your
            weekend &mdash; Ori finds the local food, events, and hidden gems you&rsquo;ll
            love, then helps you get out the door.
          </p>
        </header>

        <Prose>
          <Section title="Your city is full of things you're missing.">
            <p>
              The best taco truck in town, the no-cover jazz night, the pop-up that&rsquo;s
              gone by Sunday &mdash; it&rsquo;s all out there, buried under ads, five-year-old
              reviews, and the same ten &ldquo;best of&rdquo; lists. Finding something great
              nearby shouldn&rsquo;t take an hour of research. It should take one text.
            </p>
          </Section>

          <Section title="Recommendation feeds aren't built for you.">
            <p>
              A feed&rsquo;s algorithm isn&rsquo;t actually for you. It&rsquo;s for the company
              profiting off you being stuck on your phone &mdash; optimized for watch time, not
              for your Friday night. You can scroll food videos for an hour and still not know
              where to eat dinner. We think discovery should end with you somewhere great, not
              with you still scrolling.
            </p>
          </Section>

          <Section title="We optimize for going out, not scrolling.">
            <p>
              There&rsquo;s no app to download, no feed to scroll, no reviews to wade through.
              You text Ori like you&rsquo;d text a friend who knows the city inside out. It
              answers with a few great options, helps you pick, and then gets out of the way so
              you can put your phone in your pocket and actually be there.
            </p>
          </Section>

          <Section title="How Ori works">
            <p>
              Tell Ori what you&rsquo;re in the mood for &mdash; &ldquo;cheap eats near me,&rdquo;
              &ldquo;something fun this Saturday,&rdquo; &ldquo;a date spot that isn&rsquo;t
              boring.&rdquo; Ori knows what&rsquo;s nearby, what&rsquo;s open, and what locals
              actually rate, and it learns your taste with every recommendation.
            </p>
            <p>
              When something clicks, Ori turns it into a real plan &mdash; the place, the
              timing, the directions &mdash; and can loop in your friends so everyone shows up
              at the same spot. You decide; Ori handles the details.
            </p>
          </Section>

          <Section title="An AI that gets you into the city — not stuck on a screen.">
            <p>
              This is the line we won&rsquo;t cross. The industry is racing to build chatbots
              designed to keep you talking to a screen for as long as possible. We think
              it&rsquo;s the attention economy repackaged.
            </p>
            <p>
              Ori is the opposite by design. It is not a companion or a feed. It has one job:
              to get you off your phone and into the best of your city &mdash; the meals, the
              nights out, the places you&rsquo;ll tell people about. Success, for us, is the
              moment you stop texting Ori because you&rsquo;re already there.
            </p>
          </Section>

          <Section title="The best of your city comes from showing up.">
            <p>
              Nobody remembers the night they spent picking a restaurant. You remember the meal,
              the show, the place you almost didn&rsquo;t try. So we changed the formula.
            </p>
            <ul>
              <li>
                <strong>Great nights start with a great pick.</strong> One trusted suggestion
                beats fifty open tabs &mdash; Ori narrows the city down to what fits you, right
                now.
              </li>
              <li>
                <strong>Local beats viral.</strong> The spots worth going to are rarely the ones
                with the biggest ad budget. Ori digs past the tourist list to where locals
                actually go.
              </li>
              <li>
                <strong>Going beats browsing.</strong> A recommendation only matters if you act
                on it &mdash; so every suggestion comes ready to become a plan, not another
                bookmark you&rsquo;ll never open.
              </li>
            </ul>
            <p>
              You bring the appetite and the curiosity. Ori finds the spot, so the only thing
              left to do is go.
            </p>
          </Section>

          <Section title="Your next favorite spot is one text away.">
            <p>Text Ori. We&rsquo;ll handle the rest.</p>
          </Section>
        </Prose>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2
        className="mb-4 text-2xl font-bold"
        style={{
          fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
          color: '#3A331D',
        }}
      >
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="space-y-4 text-base leading-relaxed text-ink/90 [&_a]:text-oriOrange [&_a]:underline [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
      style={{ fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif' }}
    >
      {children}
    </div>
  );
}
